import React, { useCallback, useState } from "react";
import { Button, TextField, Card, CardContent } from "@mui/material";
import AuthHeaderLogo from "../../Common/AuthHeaderLogo/AuthHeaderLogo";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { requestPasswordResetLink } from "../../../services/accountService";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

function RequestForm({
    email,
    setEmail,
    error,
    googleSiteKey,
    recaptchaIsValid,
    onRecaptcha,
    onRecaptchaError,
    onSubmit,
}) {
    return (
        <CardContent>
            <div className="auth-title">
                <h2>Reset your password</h2>
                <p>
                    Enter your email address and we will send you a recovery
                    link.
                </p>
            </div>
            <div className="auth-form">
                <div className="form-row">
                    <TextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        onChange={(e) => {
                            setEmail(e.currentTarget.value.trim());
                        }}
                        value={email}
                    />
                </div>
                {!!googleSiteKey && (
                    <div className="form-row">
                        <ReCAPTCHA
                            sitekey={googleSiteKey}
                            onErrored={onRecaptchaError}
                            onChange={onRecaptcha}
                        />
                    </div>
                )}
                <div className="form-action">
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={onSubmit}
                        disabled={!email || !recaptchaIsValid}
                        disableElevation={true}
                    >
                        Request recovery link
                    </Button>
                </div>
            </div>
            {error && (
                <div className="form-row-last">
                    <div className="alert alert-error">{error}</div>
                </div>
            )}
        </CardContent>
    );
}

function CheckEmail({ email }) {
    return (
        <CardContent>
            <div className="auth-form">
                <p>
                    If the email address <strong>{email}</strong> is associated
                    with a <strong>Centric</strong> account, you will shortly
                    receive an email with instructions on how to reset your
                    password.
                </p>
                <p>
                    {" "}
                    If you do not receive this email, please check your junk
                    mail folder or contact <strong>Support</strong> for further
                    assistance.
                </p>
            </div>
        </CardContent>
    );
}
export default function RequestLink() {
    const googleSiteKey = process.env.REACT_APP_GOOGLE_SITE_KEY;
    const [email, setEmail] = useState("");
    const [expiry, setExpiry] = useState();
    const [recaptchaIsValid, setRecaptchaIsValid] = useState(false);
    const [error, setError] = useState("");

    const handleSetEmail = useCallback(
        (newValue) => {
            setEmail(newValue);
            error && setError("");
        },
        [error, setError]
    );

    const handleSubmitRecaptcha = () => setRecaptchaIsValid(true);
    const handleRecaptchaError = () => setRecaptchaIsValid(false);

    const handleSubmit = useCallback(async () => {
        if (!email) return;

        try {
            const { data } = await requestPasswordResetLink(email);
            setExpiry(data.token_expiry || null);
        } catch (ex) {
            if (ex.response && ex.response.status === 400) {
                const { error, error_description } = ex.response.data;
                setError(error_description || error);
            }
        }
    }, [setExpiry, setError, email]);

    return (
        <div className="form auth-card-container request-link">
            <Card className="card" data-cy="card_request_link">
                <AuthHeaderLogo />
                {!!expiry && <CheckEmail email={email} />}
                {!expiry && (
                    <RequestForm
                        email={email}
                        setEmail={handleSetEmail}
                        googleSiteKey={googleSiteKey}
                        recaptchaIsValid={recaptchaIsValid}
                        onRecaptcha={handleSubmitRecaptcha}
                        onRecaptchaError={handleRecaptchaError}
                        error={error}
                        onSubmit={handleSubmit}
                    />
                )}
            </Card>
            <div className="auth-form-links">
                <ul className="footer-links">
                    <li>
                        <Link to="/login">Sign in</Link>
                    </li>
                </ul>
            </div>
        </div>
    );
}
